// Site.tsx
//
// Aba "Site" de Lojas. É aqui que o dono do restaurante monta a landing que
// substitui o Linktree dele:
//
//   <organizacao>.alfredo.food   a página da REDE, com link para cada marca
//   <company>.alfredo.food       a LANDING da marca — cardápio, unidades, pedir
//
// A UNIDADE não tem página: ela é DESTINO de pedido, escolhida pelo cliente
// dentro da landing, por proximidade. Por isso a lista de escopos aqui tem
// organização e empresas, e não lojas.
//
// Divisão de trabalho, no molde de LayoutImpressaoPage + EditorLayoutComanda:
// esta section SABE SALVAR (service, token, avisos); `EditorDoSite`, `Handle` e
// `Unidades` só editam e avisam.
//
// De onde os escopos vêm: do `decryptedInfoToken`, não de HTTP — mesmo motivo de
// MeiosPagamento.tsx (não existe rota de listagem que traga a travessia).

import React, { useEffect, useMemo, useState } from "react";
import * as AlfredoStyles from "../../../../styles/components/configuration/sections/storesSection/Site";
import EditorDoSite, { BlocoEditavel } from "./EditorDoSite";
import Handle from "./Handle";
import Paginas from "./Paginas";
import Unidades from "./Unidades";
import { DescritorServido, siteService } from "../../../../services/site.service";
import {
  EscopoDoSite,
  MarcaDaLanding,
  TEMA_PADRAO,
  TemaDoSite,
  UnidadeDaLanding,
} from "../../../../comum/site";

interface Props {
  decryptedInfoToken: any;
}

interface Escopo {
  escopo: EscopoDoSite;
  escopoUuid: string;
  nome: string;
}

type Aviso = { tom: "ok" | "erro" | "atencao"; texto: string };

const Site: React.FC<Props> = ({ decryptedInfoToken }) => {
  /** Organização + empresas. A filial não entra: ela não tem site. */
  const escopos: Escopo[] = useMemo(() => {
    const organizacao = decryptedInfoToken?.organization;
    if (!organizacao) return [];
    const lista: Escopo[] = [
      {
        escopo: "organization",
        escopoUuid: organizacao.organizationUuid,
        nome: organizacao.organizationName,
      },
    ];
    for (const empresa of organizacao.companies ?? []) {
      lista.push({
        escopo: "company",
        escopoUuid: empresa.companyUuid,
        nome: empresa.companyName,
      });
    }
    return lista;
  }, [decryptedInfoToken]);

  const [selecionado, setSelecionado] = useState<string>("");
  const [catalogo, setCatalogo] = useState<DescritorServido[]>([]);
  const [carregando, setCarregando] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [aviso, setAviso] = useState<Aviso | null>(null);

  const [handleAtual, setHandleAtual] = useState<string | null>(null);
  const [aposentados, setAposentados] = useState<any[]>([]);
  const [tema, setTema] = useState<TemaDoSite>({ ...TEMA_PADRAO });
  const [marca, setMarca] = useState<MarcaDaLanding | null>(null);
  const [unidades, setUnidades] = useState<UnidadeDaLanding[]>([]);
  const [paginaUuid, setPaginaUuid] = useState<string>("");
  const [blocos, setBlocos] = useState<BlocoEditavel[]>([]);
  const [doPadrao, setDoPadrao] = useState(false);

  /**
   * A PÁGINA ABERTA e a lista de páginas do escopo.
   *
   * O caminho era `'/'` fixo aqui, e por isso o dono só tinha a home: uma
   * landing de restaurante quase sempre quer `/sobre` ou `/contato`.
   */
  const [caminho, setCaminho] = useState<string>("/");
  const [paginas, setPaginas] = useState<any[]>([]);
  const [pagina, setPagina] = useState<any | null>(null);

  const atual = escopos.find(
    (item) => `${item.escopo}:${item.escopoUuid}` === selecionado
  );

  useEffect(() => {
    if (!selecionado && escopos.length > 0) {
      // Abre na primeira MARCA, não na rede: é a landing da marca que substitui
      // o Linktree e é o que o dono vem procurar.
      const primeiraCompany = escopos.find((item) => item.escopo === "company");
      const escolhido = primeiraCompany ?? escopos[0];
      setSelecionado(`${escolhido.escopo}:${escolhido.escopoUuid}`);
    }
  }, [escopos, selecionado]);

  useEffect(() => {
    let ativo = true;
    siteService
      .catalogo()
      .then((dados) => {
        if (ativo) setCatalogo(dados.blocos ?? []);
      })
      .catch(() => {
        if (ativo) {
          setAviso({
            tom: "erro",
            texto:
              "Não consegui ler o catálogo de blocos do servidor. Recarregue a página.",
          });
        }
      });
    return () => {
      ativo = false;
    };
  }, []);

  const carregar = React.useCallback(async () => {
    if (!atual) return;
    setCarregando(true);
    setAviso(null);
    try {
      const [site, resposta] = await Promise.all([
        siteService.buscar(atual.escopo, atual.escopoUuid),
        siteService.buscarPagina(atual.escopo, atual.escopoUuid, caminho),
      ]);

      setHandleAtual(site?.handle ?? null);
      setAposentados(site?.aposentados ?? []);
      setPaginas(site?.paginas ?? []);
      setMarca(resposta?.marca ?? site?.marca ?? null);
      setUnidades(resposta?.unidades ?? site?.unidades ?? []);
      setTema(resposta?.tema ?? site?.tema ?? { ...TEMA_PADRAO });
      setPagina(resposta?.pagina ?? null);
      setPaginaUuid(resposta?.pagina?.paginaUuid ?? "");
      setBlocos(
        (resposta?.blocos ?? []).map((bloco: any) => ({
          tipo: bloco.tipo,
          config: bloco.config ?? {},
        }))
      );
      setDoPadrao(resposta?.doPadrao === true);
    } catch (erro: any) {
      setAviso({
        tom: "erro",
        texto:
          erro?.response?.data?.message ?? "Não foi possível carregar o site desta marca.",
      });
    } finally {
      setCarregando(false);
    }
  }, [atual, caminho]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  /**
   * RECARREGAR SÓ AS UNIDADES — sem passar perto do editor.
   *
   * O defeito que isto conserta: `Unidades` chamava `carregar()` inteiro ao
   * salvar, e `carregar()` trocava o `key` do `EditorDoSite`, que remontava e
   * JOGAVA FORA o rascunho da tela. O gatilho mais comum era o próprio aviso do
   * sistema: o dono liga "esta unidade recebe pedidos", o sistema manda
   * acrescentar o bloco "Peça agora" — e a mesma ação apagava as edições dele.
   */
  const recarregarUnidades = React.useCallback(async () => {
    if (!atual) return;
    try {
      const site = await siteService.buscar(atual.escopo, atual.escopoUuid);
      setUnidades(site?.unidades ?? []);
    } catch {
      // Silêncio de propósito: a gravação da unidade já respondeu, e o aviso
      // dela é o que importa. Falhar a releitura não pode virar um erro
      // vermelho por cima de um "salvo" verde.
    }
  }, [atual]);

  const salvarHandle = async (handle: string) => {
    if (!atual) return;
    setSalvando(true);
    try {
      const resposta = await siteService.definirHandle(atual.escopo, atual.escopoUuid, handle);
      setHandleAtual(resposta?.handle ?? handle);
      setAposentados(resposta?.handleAnterior
        ? [{ handle: resposta.handleAnterior, aposentadoEm: new Date().toISOString() }, ...aposentados]
        : aposentados);
      setAviso({ tom: "ok", texto: resposta?.mensagem ?? "Endereço definido." });
      // NÃO chama `carregar()`: trocar o endereço não muda bloco nenhum, e
      // recarregar aqui era mais um caminho que apagava o rascunho da tela.
    } catch (erro: any) {
      setAviso({
        tom: "erro",
        texto: erro?.response?.data?.message ?? "Não foi possível definir o endereço.",
      });
    } finally {
      setSalvando(false);
    }
  };

  const salvarRascunho = async (novos: BlocoEditavel[]) => {
    if (!atual || !paginaUuid) return;
    setSalvando(true);
    try {
      // O tema vai junto: para o dono, "salvar" é um ato só. Separar em dois
      // botões faria a cor ficar num estado e os blocos em outro.
      await siteService.salvarTema(atual.escopo, atual.escopoUuid, tema);
      const resposta = await siteService.salvarPagina(
        atual.escopo,
        atual.escopoUuid,
        paginaUuid,
        novos
      );
      setBlocos(novos);
      setDoPadrao(false);
      setAviso({ tom: "ok", texto: resposta?.mensagem ?? "Rascunho salvo." });
    } catch (erro: any) {
      setAviso({
        tom: "erro",
        texto: erro?.response?.data?.message ?? "Não foi possível salvar o rascunho.",
      });
    } finally {
      setSalvando(false);
    }
  };

  /**
   * PUBLICAR = SALVAR O QUE ESTÁ NA TELA E ENTÃO PUBLICAR.
   *
   * Antes esta função mandava só o `paginaUuid` e o servidor publicava o que
   * estava no BANCO. Duas consequências, as duas medidas:
   *
   *  1. A PRIMEIRA publicação morria em 400. A página nova abre com a landing
   *     padrão MONTADA EM MEMÓRIA (blocos `padrao-N`, nunca gravados); publicar
   *     sem salvar achava zero rascunhos e respondia "Não há nenhum bloco no
   *     rascunho" — com seis blocos na tela do dono.
   *  2. Quem editava e publicava sem salvar lia "Publicado." e o site continuava
   *     com o conteúdo antigo. Mentir para o dono é o pior modo de falhar: ele
   *     divulga um link que não tem o que ele acabou de escrever.
   *
   * A sequência é a mesma que o dono faria à mão (salvar, depois publicar), e a
   * ordem importa: se o salvar falhar, NADA é publicado.
   */
  const publicar = async (novos: BlocoEditavel[]) => {
    if (!atual || !paginaUuid) return;
    setSalvando(true);
    try {
      await siteService.salvarTema(atual.escopo, atual.escopoUuid, tema);
      await siteService.salvarPagina(
        atual.escopo,
        atual.escopoUuid,
        paginaUuid,
        novos
      );
      setBlocos(novos);
      setDoPadrao(false);

      const resposta = await siteService.publicar(atual.escopo, atual.escopoUuid, paginaUuid);
      setAviso({
        tom: "ok",
        texto: `${resposta?.mensagem ?? "Publicado."}${
          handleAtual ? ` Veja em ${handleAtual}.alfredo.food${caminho === "/" ? "" : caminho}.` : ""
        }`,
      });
    } catch (erro: any) {
      setAviso({
        tom: "erro",
        texto: erro?.response?.data?.message ?? "Não foi possível publicar.",
      });
    } finally {
      setSalvando(false);
    }
  };

  /** Cria a página e ABRE nela — criar e não ir junto seria meio caminho. */
  const criarPagina = async (novoCaminho: string) => {
    if (!atual) return;
    setSalvando(true);
    try {
      const resposta = await siteService.criarPagina(
        atual.escopo,
        atual.escopoUuid,
        novoCaminho
      );
      setAviso({ tom: "ok", texto: resposta?.mensagem ?? "Página criada." });
      setCaminho(resposta?.pagina?.caminho ?? novoCaminho);
    } catch (erro: any) {
      setAviso({
        tom: "erro",
        texto: erro?.response?.data?.message ?? "Não foi possível criar a página.",
      });
    } finally {
      setSalvando(false);
    }
  };

  /** Título, descrição e imagem de OG. É o que o Google e o WhatsApp mostram. */
  const salvarMetadados = async (dados: {
    titulo: string;
    descricao: string;
    imagemOg: string;
  }) => {
    if (!atual || !paginaUuid) return;
    setSalvando(true);
    try {
      const resposta = await siteService.atualizarPagina(
        atual.escopo,
        atual.escopoUuid,
        paginaUuid,
        dados
      );
      setPagina(resposta?.pagina ?? pagina);
      setAviso({ tom: "ok", texto: resposta?.mensagem ?? "Salvo." });
    } catch (erro: any) {
      setAviso({
        tom: "erro",
        texto: erro?.response?.data?.message ?? "Não foi possível salvar o título.",
      });
    } finally {
      setSalvando(false);
    }
  };

  const arquivarPagina = async (uuid: string, arquivada: boolean) => {
    if (!atual) return;
    setSalvando(true);
    try {
      const resposta = await siteService.arquivarPagina(
        atual.escopo,
        atual.escopoUuid,
        uuid,
        arquivada
      );
      setAviso({ tom: "ok", texto: resposta?.mensagem ?? "Pronto." });
      // A lista muda de estado; recarregar aqui é seguro porque arquivar é uma
      // ação sobre OUTRA página, não sobre a que está aberta no editor.
      const site = await siteService.buscar(atual.escopo, atual.escopoUuid);
      setPaginas(site?.paginas ?? []);
    } catch (erro: any) {
      setAviso({
        tom: "erro",
        texto: erro?.response?.data?.message ?? "Não foi possível arquivar a página.",
      });
    } finally {
      setSalvando(false);
    }
  };

  if (!decryptedInfoToken) {
    return (
      <AlfredoStyles.FieldsContainer>
        <AlfredoStyles.LoadingContainer>
          <AlfredoStyles.LoadingSpinner />
          <div>Carregando…</div>
        </AlfredoStyles.LoadingContainer>
      </AlfredoStyles.FieldsContainer>
    );
  }

  return (
    <AlfredoStyles.FieldsContainer>
      <AlfredoStyles.Header>
        <AlfredoStyles.HeaderContent>
          <AlfredoStyles.HeaderTitle>
            <AlfredoStyles.MaterialIcons style={{ fontSize: "calc(var(--u) * 36)" }}>
              public
            </AlfredoStyles.MaterialIcons>
            Site
          </AlfredoStyles.HeaderTitle>
          <AlfredoStyles.HeaderSubtitle>
            O site público do seu restaurante — endereço, aparência, blocos e as
            unidades que recebem pedido
          </AlfredoStyles.HeaderSubtitle>
        </AlfredoStyles.HeaderContent>

        {escopos.length > 1 && (
          <AlfredoStyles.Seletor
            value={selecionado}
            onChange={(e) => setSelecionado(e.target.value)}
            style={{ maxWidth: "calc(var(--u) * 300)" }}
          >
            {escopos.map((item) => (
              <option key={item.escopoUuid} value={`${item.escopo}:${item.escopoUuid}`}>
                {item.escopo === "organization" ? `${item.nome} (a rede)` : item.nome}
              </option>
            ))}
          </AlfredoStyles.Seletor>
        )}
      </AlfredoStyles.Header>

      {escopos.length === 0 ? (
        <AlfredoStyles.EmptyState>
          <AlfredoStyles.EmptyStateIcon>🌐</AlfredoStyles.EmptyStateIcon>
          <AlfredoStyles.EmptyStateTitle>Nada para publicar ainda</AlfredoStyles.EmptyStateTitle>
          <AlfredoStyles.EmptyStateDescription>
            Cadastre a empresa em "Estabelecimentos" para montar o site dela.
          </AlfredoStyles.EmptyStateDescription>
        </AlfredoStyles.EmptyState>
      ) : (
        <>
          {aviso && <AlfredoStyles.Aviso $tom={aviso.tom}>{aviso.texto}</AlfredoStyles.Aviso>}

          <Handle
            nomeDaMarca={atual?.nome ?? ""}
            handleAtual={handleAtual}
            aposentados={aposentados}
            salvando={salvando}
            onSalvar={salvarHandle}
          />

          {carregando || !marca ? (
            <AlfredoStyles.LoadingContainer>
              <AlfredoStyles.LoadingSpinner />
              <div>Carregando o site…</div>
            </AlfredoStyles.LoadingContainer>
          ) : (
            <>
              <Paginas
                paginas={paginas}
                pagina={pagina}
                caminho={caminho}
                salvando={salvando}
                onAbrir={setCaminho}
                onCriar={criarPagina}
                onSalvarMetadados={salvarMetadados}
                onArquivar={arquivarPagina}
              />

              {/*
                O `key` é o escopo + o caminho, e NÃO um contador de recarga.
                Com o contador, qualquer releitura remontava o editor e jogava
                fora o rascunho da tela — inclusive a releitura disparada por
                salvar uma UNIDADE, que é a ação que o próprio sistema pede logo
                depois de ligar "esta unidade recebe pedidos".
                Agora só troca de página ou de marca reabre o editor, que é
                quando reabrir é o certo.
              */}
              <EditorDoSite
                key={`${selecionado}-${caminho}`}
                marca={marca}
                unidades={unidades}
                tema={tema}
                catalogo={catalogo}
                blocosIniciais={blocos}
                doPadrao={doPadrao}
                salvando={salvando}
                onMudarTema={setTema}
                onSalvar={salvarRascunho}
                onPublicar={publicar}
              />

              {atual?.escopo === "company" && (
                <div style={{ marginTop: "var(--sp-24)" }}>
                  <AlfredoStyles.HeaderTitle
                    style={{ fontSize: "var(--fs-xl)", marginBottom: "var(--sp-12)" }}
                  >
                    Unidades
                  </AlfredoStyles.HeaderTitle>
                  <Unidades unidades={unidades} onSalvo={recarregarUnidades} />
                </div>
              )}
            </>
          )}
        </>
      )}
    </AlfredoStyles.FieldsContainer>
  );
};

export default Site;
